import { Component, DestroyRef, HostBinding, HostListener, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Router, NavigationEnd } from '@angular/router';
import { filter, pairwise, take } from 'rxjs/operators';
import { Language, NavigationItem, UserProfile, AppBarProfileAction, EkoLanguageService, EkoThemeService, EkoBrowserTitleService, hideIndexLoadingScreen } from '@ekowitryna/ui';

import { MenuItem } from 'primeng/api';
import { PrimeNG } from 'primeng/config';
import { environment } from '../environments/environment';
import { KeycloakService } from 'keycloak-angular';
import { KeycloakProfile } from 'keycloak-js';
import { TranslateService } from '@ngx-translate/core';

import packageJson from '../../package.json';
import { CompanyContextService } from './core/services/company-context.service';
import { AccountSetupService } from './core/services/account-setup.service';
import { PackageDisplayService } from './core/services/package-display.service';
import { EKOWITRYNA_SUBSCRIPTION_URL } from './core/constants/subscription-url';

const MOBILE_BREAKPOINT_PX = 1024; // zgodne z Tailwind lg
const SUPPORTED_LANGUAGE_CODES = ['pl', 'en', 'de', 'es', 'uk', 'fr'] as const;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  standalone: false
})
export class AppComponent implements OnInit {

  private readonly accountSetupService = inject(AccountSetupService);
  private readonly packageDisplay = inject(PackageDisplayService);
  private readonly darkModeService = inject(EkoThemeService);
  private readonly languageService = inject(EkoLanguageService);
  private readonly browserTitle = inject(EkoBrowserTitleService);

  isAdminMode: boolean = false;
  isInitializing = true;
  appVersion: string = packageJson.version;

  sidebarCollapsed = false;
  isMobile = false;
  keycloakUrl: string = environment.keycloakUrl;
  keycloakRealm: string = environment.realm;
  keycloakClientId: string = environment.clientId;
  pageTitle = 'Ekowitryna';
  availableLanguages: Language[] = [
    { code: 'pl', name: 'Polski' },
    { code: 'en', name: 'English', flag: 'gb' },
    { code: 'de', name: 'Deutsch' },
    { code: 'es', name: 'Español' },
    { code: 'uk', name: 'Українська', flag: 'ua' },
    { code: 'fr', name: 'Français' }
  ];
  currentRoute = '';
  get currentLanguage(): string {
    return this.languageService.currentLanguage;
  }

  navigationItems: NavigationItem[] = [];
  userMenuItems: MenuItem[] = [];
  currentUser: UserProfile | null = null;
  authenticated = false;
  profileActions: AppBarProfileAction[] = [];
  profileExternalUrl = `${environment.platformWebUrl.replace(/\/$/, '')}/profile`;
  readonly subscriptionUrl = EKOWITRYNA_SUBSCRIPTION_URL;
  requireFirstConfiguration = false;

  get showFirstConfigurationOverlay(): boolean {
    return this.requireFirstConfiguration && this.authenticated;
  }

  /** App bar labels (translated) */
  loginLabel = '';
  userMenuLabel = '';
  myProfileLabel = '';
  logoutLabel = '';
  buySubscriptionLabel = '';
  themeLabel = '';
  languageLabel = '';
  toggleSidebarLabel = '';

  private keycloakReady = false;
  private profileReady = false;
  private i18nReady = false;

  @HostBinding('class.sidebar-open-mobile') get sidebarOpenMobile(): boolean {
    return this.isMobile && !this.sidebarCollapsed;
  }

  @HostBinding('attr.data-initialized')
  get shellInitialized(): string | null {
    return this.isInitializing ? null : 'true';
  }

  @HostListener('window:resize')
  onResize(): void {
    this.isMobile = window.innerWidth < MOBILE_BREAKPOINT_PX;
  }

  constructor(
    private router: Router,
    private keycloakService: KeycloakService,
    private primeNG: PrimeNG,
    private translate: TranslateService,
    private readonly companyContext: CompanyContextService,
    private readonly destroyRef: DestroyRef
  ) {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      const url = event.urlAfterRedirects;
      this.currentRoute = url;
      this.isAdminMode = url.startsWith('/admin');
    });

    this.companyContext.selectedCompanyId$
      .pipe(
        pairwise(),
        filter(
          ([previous, current]) =>
            previous != null && current != null && String(previous) !== String(current)
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        void this.router.navigate(['/reports'], { replaceUrl: true });
      });
  }

  ngOnInit(): void {
    // Keycloak is already initialized via APP_INITIALIZER (legacy KeycloakAngularModule).
    this.keycloakReady = true;

    this.isMobile = window.innerWidth < MOBILE_BREAKPOINT_PX;
    if (this.isMobile) {
      this.sidebarCollapsed = true;
    }

    this.currentRoute = this.router.url;

    this.accountSetupService.setupRequired$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((state) => {
        if (state.loading) {
          return;
        }
        this.requireFirstConfiguration = state.required;
      });

    void this.loadUserProfile().finally(() => {
      this.profileReady = true;
      this.tryFinishInitialization();
    });

    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.refreshNavigationAndProfileLabels());
    this.refreshNavigationAndProfileLabels();
  }

  private refreshNavigationAndProfileLabels(): void {
    const keys = [
      'headerProfile.login',
      'headerProfile.consultations',
      'headerProfile.myProfile',
      'headerProfile.userMenu',
      'headerProfile.logout',
      'headerProfile.buySubscription',
      'settings.theme',
      'common.language',
      'a11y.toggleSidebar',
      'menu.calculator'
    ];

    this.translate.get(keys).subscribe(translations => {
      this.loginLabel = translations['headerProfile.login'] ?? '';
      this.userMenuLabel = translations['headerProfile.userMenu'] ?? '';
      this.myProfileLabel = translations['headerProfile.myProfile'] ?? '';
      this.logoutLabel = translations['headerProfile.logout'] ?? '';
      this.buySubscriptionLabel = translations['headerProfile.buySubscription'] ?? '';
      this.themeLabel = translations['settings.theme'] ?? '';
      this.languageLabel = translations['common.language'] ?? '';
      this.toggleSidebarLabel = translations['a11y.toggleSidebar'] ?? '';

      this.profileActions = [
        { label: translations['headerProfile.consultations'] ?? '', action: 'consultations', icon: 'pi pi-calendar' }
      ];

      this.navigationItems = [
        {
          label: translations['menu.calculator'] ?? '',
          icon: 'pi pi-calculator',
          routerLink: '/reports'
        }
      ];
      this.browserTitle.setNavigationItems(this.navigationItems);

      if (!this.i18nReady) {
        this.i18nReady = true;
        this.tryFinishInitialization();
      }
    });
  }

  private tryFinishInitialization(): void {
    if (this.keycloakReady && this.profileReady && this.i18nReady) {
      this.isInitializing = false;
      hideIndexLoadingScreen();
    }
  }

  async loadUserProfile() {
    this.authenticated = await this.keycloakService.isLoggedIn();

    if (!this.authenticated) {
      this.darkModeService.initFromStorageOrSystem();
      this.currentUser = null;
      this.requireFirstConfiguration = false;
      return;
    }

    await this.darkModeService.syncFromProfile();
    await this.languageService.syncFromProfile([...SUPPORTED_LANGUAGE_CODES]);
    void this.accountSetupService.check();

    if (this.authenticated) {
      try {
        const profile: KeycloakProfile = await this.keycloakService.loadUserProfile();
        const roles = this.keycloakService.getUserRoles();
        
        this.currentUser = {
          name: `${profile.firstName || ''} ${profile.lastName || ''}`.trim() || profile.username || '',
          email: profile.email,
          avatar: `https://ui-avatars.com/api/?size=128&background=random&name=${profile.firstName}+${profile.lastName}`,
          role: roles && roles.length > 0 ? roles[0] : undefined
        };
        this.applySubscriptionBadge();
      } catch (error) {
        console.error('Error loading user profile:', error);
        this.currentUser = null;
      }
    }
  }


  private applySubscriptionBadge(): void {
    if (!this.currentUser) {
      return;
    }

    this.currentUser = { ...this.currentUser, subscriptionBadge: 'Free' };
    this.packageDisplay.getPackageDisplayName$().pipe(take(1)).subscribe({
      next: (name) => {
        if (!this.currentUser) {
          return;
        }
        const badge = name?.trim() || 'Free';
        if (this.currentUser.subscriptionBadge === badge) {
          return;
        }
        this.currentUser = { ...this.currentUser, subscriptionBadge: badge };
      },
      error: () => {
        if (!this.currentUser) {
          return;
        }
        this.currentUser = { ...this.currentUser, subscriptionBadge: 'Free' };
      },
    });
  }

  toggleNightMode(): void {

  }

  onProfileAction(action: string) {
    if (action === 'logout') {
      this.authenticated = false;
      this.currentUser = null;
      this.keycloakService.logout(window.location.origin);
    } else if (action === 'login') {
      this.keycloakService.login();
    } else if (action === 'consultations') {
      window.open('https://ekowitryna.pl/konsultacje/', '_blank');
    }
  }

  onProfileMenuClick(item: string | MenuItem) {
    if (typeof item !== 'string' && item.command) {
      item.command({} as any);
    }
  }

  onMenuClick(item: NavigationItem) {
    if (item.routerLink && !item.disabled) {
      const route = Array.isArray(item.routerLink) ? item.routerLink : [item.routerLink];
      this.router.navigate(route);
    }
    this.closeSidebarMobile();
  }

  onMenuClosed() {
    this.closeSidebarMobile();
  }

  onLanguageChange(lang: string): void {
    this.languageService.setLanguage(lang);
  }


  onSidebarToggle() {
    this.sidebarCollapsed = !this.sidebarCollapsed;
  }

  closeSidebarMobile(): void {
    if (this.isMobile) {
      this.sidebarCollapsed = true;
    }
  }

  login() {
    this.keycloakService.login();
  }

}
